// iter155 — Dialog to split a legacy single-line opening-month rice receipt
// into dated receipts. Validates total matches receivedRice within
// ±0.001 kg before saving. Also lets the user add a single dated receipt
// going forward or undo the last mutation within 7 days.

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save, Undo2, X, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_KEYS = ["balVatika", "primary", "upperPrimary", "highSchool"];
const sumClass = (obj) => CLASS_KEYS.reduce((s, k) => s + Number((obj || {})[k] || 0), 0);
const kg3 = (v) => Number(v || 0).toFixed(3);

function daysInMonth(y, m) {
  return new Date(y, m, 0).getDate();
}

export default function RiceReceiptsDialog({ fund, year, month, isHardLocked, onClose, onSaved }) {
  const schoolId = fund?.schoolId || "";
  const receivedTotal = sumClass(fund?.receivedRice);
  const existingReceipts = useMemo(
    () => (Array.isArray(fund?.riceReceipts) ? fund.riceReceipts : []).map((r) => ({
      date: r.date, kg: Number(r.kg) || 0, source: r.source || "", refNo: r.refNo || "", note: r.note || "",
    })),
    [fund?.riceReceipts],
  );
  const [rows, setRows] = useState(existingReceipts.length ? existingReceipts : [
    { date: `${year}-${String(month).padStart(2, "0")}-01`, kg: receivedTotal, source: "", refNo: "", note: "" },
  ]);
  const [saving, setSaving] = useState(false);
  const [undoing, setUndoing] = useState(false);

  useEffect(() => {
    setRows(existingReceipts.length ? existingReceipts : [
      { date: `${year}-${String(month).padStart(2, "0")}-01`, kg: receivedTotal, source: "", refNo: "", note: "" },
    ]);
  }, [existingReceipts, year, month, receivedTotal]);

  const totalKg = useMemo(() => rows.reduce((s, r) => s + Number(r.kg || 0), 0), [rows]);
  const diff = Math.abs(totalKg - receivedTotal);
  const inTolerance = diff <= 0.001;
  const lastDay = daysInMonth(year, month);
  const invalidDate = rows.some((r) => {
    const [yy, mm, dd] = (r.date || "").split("-").map((v) => parseInt(v, 10));
    return !(yy === year && mm === month && dd >= 1 && dd <= lastDay);
  });
  const hasAudit = Array.isArray(fund?.riceReceiptsAudit) && fund.riceReceiptsAudit.length > 0;
  const lastAudit = hasAudit ? fund.riceReceiptsAudit[fund.riceReceiptsAudit.length - 1] : null;
  const undoAvailable = !!(lastAudit && lastAudit.undoDeadline && new Date(lastAudit.undoDeadline).getTime() > Date.now() && !isHardLocked);

  const addRow = () => setRows((r) => [...r, {
    date: `${year}-${String(month).padStart(2, "0")}-01`, kg: 0, source: "", refNo: "", note: "",
  }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const handleSplit = async () => {
    if (isHardLocked) return toast.error("Month is hard-locked");
    if (invalidDate) return toast.error("Every receipt date must fall inside this month");
    if (!inTolerance) return toast.error(`Receipts sum (${kg3(totalKg)} kg) must match received total (${kg3(receivedTotal)} kg) within 0.001 kg`);
    try {
      setSaving(true);
      await axios.post(`${API}/fund-balances/split-rice`, {
        schoolId, year, month,
        receipts: rows.map((r) => ({
          date: r.date, kg: Number(r.kg || 0),
          source: r.source || "", refNo: r.refNo || "", note: r.note || "",
        })),
      });
      toast.success(`Saved ${rows.length} dated receipt${rows.length === 1 ? "" : "s"} · 7-day undo available`);
      await onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not split receipts");
    } finally {
      setSaving(false);
    }
  };

  const handleUndo = async () => {
    try {
      setUndoing(true);
      await axios.post(`${API}/fund-balances/receipt/undo`, { schoolId, year, month });
      toast.success("Last receipt change reverted");
      await onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not undo");
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/60 p-4" data-testid="rice-receipts-dialog">
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-border bg-sand-50 px-5 py-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-ink-900">Dated Rice Receipts · {year}-{String(month).padStart(2, "0")}</h2>
            <p className="text-xs text-ink-700">
              Split the monthly received total ({kg3(receivedTotal)} kg) into actual dated receipts.
              Save requires sum to match within ±0.001 kg.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="rice-receipts-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {isHardLocked && (
          <div className="flex items-center gap-2 border-b border-rose-200 bg-rose-50 px-5 py-2 text-xs font-semibold text-rose-700" data-testid="rice-receipts-locked">
            <Lock className="h-3.5 w-3.5" />
            Month is hard-locked — read-only. Unlock to edit receipts.
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto p-5">
          <table className="w-full text-sm" data-testid="rice-receipts-table">
            <thead className="bg-sand-100 text-left text-ink-700">
              <tr>
                <th className="p-2 text-xs font-semibold uppercase tracking-wider">#</th>
                <th className="p-2 text-xs font-semibold uppercase tracking-wider">Date</th>
                <th className="p-2 text-right text-xs font-semibold uppercase tracking-wider">kg</th>
                <th className="p-2 text-xs font-semibold uppercase tracking-wider">Source</th>
                <th className="p-2 text-xs font-semibold uppercase tracking-wider">Ref No</th>
                <th className="p-2 text-xs font-semibold uppercase tracking-wider">Note</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border" data-testid={`rice-receipts-row-${i}`}>
                  <td className="p-1.5 text-xs text-ink-700">{i + 1}</td>
                  <td className="p-1.5">
                    <input
                      type="date" min={`${year}-${String(month).padStart(2, "0")}-01`}
                      max={`${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`}
                      value={r.date}
                      disabled={isHardLocked}
                      data-testid={`rice-receipts-row-${i}-date`}
                      onChange={(e) => updateRow(i, { date: e.target.value })}
                      className="h-9 w-full rounded border border-border bg-white px-2 text-sm"
                    />
                  </td>
                  <td className="p-1.5">
                    <input
                      type="number" step="0.001" min="0" value={r.kg}
                      disabled={isHardLocked}
                      data-testid={`rice-receipts-row-${i}-kg`}
                      onChange={(e) => updateRow(i, { kg: Number(e.target.value) })}
                      className="h-9 w-full rounded border border-border bg-white px-2 text-right font-mono text-sm"
                    />
                  </td>
                  <td className="p-1.5">
                    <input
                      type="text" value={r.source} placeholder="FCI / NGO"
                      disabled={isHardLocked}
                      data-testid={`rice-receipts-row-${i}-source`}
                      onChange={(e) => updateRow(i, { source: e.target.value })}
                      className="h-9 w-full rounded border border-border bg-white px-2 text-sm"
                    />
                  </td>
                  <td className="p-1.5">
                    <input
                      type="text" value={r.refNo} placeholder="Receipt / Challan"
                      disabled={isHardLocked}
                      data-testid={`rice-receipts-row-${i}-refno`}
                      onChange={(e) => updateRow(i, { refNo: e.target.value })}
                      className="h-9 w-full rounded border border-border bg-white px-2 text-sm"
                    />
                  </td>
                  <td className="p-1.5">
                    <input
                      type="text" value={r.note}
                      disabled={isHardLocked}
                      data-testid={`rice-receipts-row-${i}-note`}
                      onChange={(e) => updateRow(i, { note: e.target.value })}
                      className="h-9 w-full rounded border border-border bg-white px-2 text-sm"
                    />
                  </td>
                  <td className="p-1.5 text-right">
                    <Button variant="ghost" size="icon" disabled={isHardLocked || rows.length === 1}
                      onClick={() => removeRow(i)}
                      data-testid={`rice-receipts-row-${i}-remove`}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3 flex items-center justify-between gap-3">
            <Button variant="outline" size="sm" disabled={isHardLocked}
              onClick={addRow}
              data-testid="rice-receipts-add-row"
            >
              <Plus className="mr-1 h-4 w-4" /> Add another date
            </Button>
            <div className="text-right">
              <p className="text-xs text-ink-700">Received total this month</p>
              <p className="font-mono text-lg font-bold text-ink-900" data-testid="rice-receipts-target">{kg3(receivedTotal)} kg</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-ink-700">Dated receipts sum</p>
              <p
                className={`font-mono text-lg font-bold ${inTolerance ? "text-moss" : "text-rose-600"}`}
                data-testid="rice-receipts-sum"
              >
                {kg3(totalKg)} kg
              </p>
              {!inTolerance && (
                <p className="text-[0.65rem] font-semibold text-rose-600" data-testid="rice-receipts-diff">
                  Δ {kg3(diff)} kg — must be ≤ 0.001 kg
                </p>
              )}
            </div>
          </div>

          {hasAudit && (
            <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="rice-receipts-audit">
              <p className="flex items-center gap-1 font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" />
                Audit · last change by {lastAudit?.byMobile || lastAudit?.byUser || "—"} at {new Date(lastAudit?.at).toLocaleString()}
              </p>
              <p className="mt-1 text-[0.7rem]">
                Action: <b>{lastAudit?.action}</b> ·
                Undo until <b>{new Date(lastAudit?.undoDeadline).toLocaleString()}</b>
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border bg-sand-50 px-5 py-3">
          <Button
            variant="outline"
            size="sm"
            disabled={!undoAvailable || undoing}
            onClick={handleUndo}
            data-testid="rice-receipts-undo"
          >
            <Undo2 className="mr-1 h-4 w-4" />
            {undoing ? "Undoing…" : "Undo last change (7-day)"}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="rice-receipts-cancel">Cancel</Button>
            <Button
              size="sm"
              disabled={isHardLocked || !inTolerance || invalidDate || saving}
              onClick={handleSplit}
              className="bg-clay text-white hover:bg-clay-dark"
              data-testid="rice-receipts-save"
            >
              <Save className="mr-1 h-4 w-4" />
              {saving ? "Saving…" : `Save ${rows.length} receipt${rows.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
